import { COLUMN_CATALOG_MAP } from './constants'
import { toNumber } from './calcResolve'

export const ALLOCATION_STRATEGIES = Object.freeze(['toParent', 'toRoot'])

export const ALLOCATION_STRATEGY_OPTIONS = [
  { value: 'toParent', label: 'Allocation to Parent' },
  { value: 'toRoot', label: 'Allocation to Total' }
]

export const WINDOW_REGISTRY = {
  allocation: {
    scope: 'row',
    description: '% allocation: this row\'s value divided by the parent (or total) row\'s value for the source measure',
    compute: (childValue, denominatorValue) => {
      const denom = toNumber(denominatorValue)
      if (denom == null || denom === 0) return null
      const child = toNumber(childValue)
      if (child == null) return null
      return child / denom
    }
  },
  variance: {
    scope: 'row',
    description: 'Difference: this row\'s value minus the parent (or total) row\'s value for the source measure',
    compute: (childValue, denominatorValue) => {
      const child = toNumber(childValue)
      const denom = toNumber(denominatorValue)
      if (child == null || denom == null) return null
      return child - denom
    }
  },
  rank: {
    scope: 'siblings',
    description: 'Ordinal rank among sibling rows by a source column value',
    defaultOptions: { sourceColumnType: 'marketValue', sourceColumnId: null, sortDirection: 'desc' },
    computeBatch: (siblingRows, col, sourceInstance) => {
      if (!sourceInstance) { siblingRows.forEach(r => { r[col.id] = null }); return }
      const direction = col.options?.sortDirection || 'desc'
      const indexed = siblingRows.map((r, i) => ({ i, val: toNumber(r[sourceInstance.id]) }))
      indexed.sort((a, b) => {
        if (a.val == null && b.val == null) return 0
        if (a.val == null) return 1
        if (b.val == null) return -1
        return direction === 'desc' ? b.val - a.val : a.val - b.val
      })
      indexed.forEach((entry, rank) => { siblingRows[entry.i][col.id] = rank + 1 })
    }
  },
  percentileRank: {
    scope: 'siblings',
    description: 'Percentile rank among sibling rows: (rank - 1) / (N - 1), where rank is ordinal position',
    defaultOptions: { sourceColumnType: 'marketValue', sourceColumnId: null, sortDirection: 'desc' },
    computeBatch: (siblingRows, col, sourceInstance) => {
      if (!sourceInstance) { siblingRows.forEach(r => { r[col.id] = null }); return }
      const n = siblingRows.length
      if (n <= 1) { siblingRows.forEach(r => { r[col.id] = toNumber(r[sourceInstance.id]) != null ? 1 : null }); return }
      const direction = col.options?.sortDirection || 'desc'
      const indexed = siblingRows.map((r, i) => ({ i, val: toNumber(r[sourceInstance.id]) }))
      indexed.sort((a, b) => {
        if (a.val == null && b.val == null) return 0
        if (a.val == null) return 1
        if (b.val == null) return -1
        return direction === 'desc' ? b.val - a.val : a.val - b.val
      })
      indexed.forEach((entry, rank) => {
        siblingRows[entry.i][col.id] = entry.val != null ? (n - 1 - rank) / (n - 1) : null
      })
    }
  },
  concentration: {
    scope: 'siblings',
    description: 'Cumulative % of siblings sorted by source column descending (concentration analysis)',
    defaultOptions: { sourceColumnType: 'marketValue', sourceColumnId: null },
    computeBatch: (siblingRows, col, sourceInstance) => {
      if (!sourceInstance) { siblingRows.forEach(r => { r[col.id] = null }); return }
      const total = siblingRows.reduce((sum, r) => sum + (toNumber(r[sourceInstance.id]) ?? 0), 0)
      if (total === 0) { siblingRows.forEach(r => { r[col.id] = null }); return }
      const indexed = siblingRows.map((r, i) => ({ i, val: toNumber(r[sourceInstance.id]) ?? 0 }))
      indexed.sort((a, b) => b.val - a.val)
      let cumulative = 0
      for (const entry of indexed) {
        cumulative += entry.val / total
        siblingRows[entry.i][col.id] = cumulative
      }
    }
  }
}

/**
 * Finds the source column instance for a window column. Prefers an exact match on
 * `sourceColumnId` (specific instance); falls back to first material column matching
 * `sourceColumnType` if the referenced instance was removed or the id is stale.
 */
function findSourceInstance (col, columnInstances, catalogMap) {
  const sourceId = col.options?.sourceColumnId
  if (sourceId) {
    const exact = columnInstances.find(c => c.id === sourceId)
    if (exact && catalogMap[exact.columnType]?.kind !== 'window' && catalogMap[exact.columnType]?.kind !== 'derived' && catalogMap[exact.columnType]?.kind !== 'comparison') {
      return exact
    }
  }
  const sourceType = col.options?.sourceColumnType
  if (!sourceType) return null
  return columnInstances.find(
    c => c.columnType === sourceType && catalogMap[c.columnType]?.kind !== 'window' && catalogMap[c.columnType]?.kind !== 'derived' && catalogMap[c.columnType]?.kind !== 'comparison'
  ) || null
}

/**
 * Mutates `row` to add values for window column instance ids.
 * Window columns read cross-row data (parent or total) rather than same-row Orion leaf calcs.
 *
 * @param {object} row - The current row being built
 * @param {object|null} parentRow - Immediate parent row (totalRow for root-level groups)
 * @param {object|null} totalRow - The household-level aggregate row
 * @param {Array} columnInstances - All column instances in the config
 * @param {object} catalogMap - COLUMN_CATALOG_MAP
 */
export function applyWindowColumns (row, parentRow, totalRow, columnInstances, catalogMap) {
  for (const col of columnInstances) {
    const catalog = catalogMap[col.columnType]
    if (!catalog || catalog.kind !== 'window') continue
    const def = WINDOW_REGISTRY[catalog.windowType]
    if (!def?.compute) continue

    const sourceInstance = findSourceInstance(col, columnInstances, catalogMap)
    if (!sourceInstance) { row[col.id] = null; continue }

    const strategy = col.options?.strategy || 'toParent'
    const denomRow = strategy === 'toRoot' ? totalRow : parentRow
    const childValue = row[sourceInstance.id]
    const denomValue = denomRow?.[sourceInstance.id]
    row[col.id] = def.compute(childValue, denomValue)
  }
}

/**
 * Second-pass window computation for columns that need access to all sibling rows at once.
 * Runs after per-row window columns are applied and all siblings in a group are built.
 */
export function applySiblingWindowColumns (siblingRows, columnInstances, catalogMap) {
  for (const col of columnInstances) {
    const catalog = catalogMap[col.columnType]
    if (!catalog || catalog.kind !== 'window') continue
    const def = WINDOW_REGISTRY[catalog.windowType]
    if (!def?.computeBatch) continue

    const sourceInstance = findSourceInstance(col, columnInstances, catalogMap)
    def.computeBatch(siblingRows, col, sourceInstance)
  }
}

/**
 * After `regenerateColumnIds`, window columns that stored a `sourceColumnIndex`
 * (the array position of their source column at serialization time) need that
 * index resolved back to the new instance id.
 */
export function resolveWindowColumnRefs (columns, catalogMap) {
  return columns.map(col => {
    const catalog = catalogMap[col.columnType]
    if (!catalog || catalog.kind !== 'window') return col

    const idx = col.options?.sourceColumnIndex
    if (idx != null && idx >= 0 && idx < columns.length) {
      return {
        ...col,
        options: {
          ...col.options,
          sourceColumnId: columns[idx].id,
          sourceColumnIndex: undefined
        }
      }
    }

    if (col.options?.sourceColumnType) {
      const match = columns.find(
        c => c.columnType === col.options.sourceColumnType &&
          catalogMap[c.columnType]?.kind !== 'window' &&
          catalogMap[c.columnType]?.kind !== 'derived'
      )
      if (match) {
        return {
          ...col,
          options: { ...col.options, sourceColumnId: match.id }
        }
      }
    }

    return col
  })
}

export function isWindowColumnType (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'window'
}
